/**
 * The licence that travels with an export.
 *
 * A zip of audio files with nothing in it saying where they came from is a zip
 * that turns up in someone's game with no way back to here. A text file costs
 * nothing, is read by whoever opens the folder, and states the one condition.
 *
 * CC BY 4.0 rather than a hand-written licence: it is a real, widely understood
 * licence that says exactly the intended thing (use it for anything, including
 * commercially, as long as you credit), and a home-made one would be less clear
 * to a studio's legal team, not more.
 */

export interface LicenceDetails {
  name: string;
  key: string;
  tempo: number;
  progression: string;
  seed?: number;
  /** The link that regenerates this exact track, if there is one. */
  link?: string;
}

const CREDIT_LINE = "Music generated with Hum by Dale Mooney (hum.dmooney.engineer)";

export function licenceText(piece: LicenceDetails): string {
  return `HUM
${"=".repeat(60)}

This audio was generated with Hum, a tool that describes music as
constraints and renders it to separate layers.

    hum.dmooney.engineer


WHAT IS IN THIS FOLDER
${"-".repeat(60)}

  pad.wav, bass.wav, rhythm.wav, melody.wav, perc.wav
      One file per layer. They are all the same length and are meant
      to be played together, all of the time, with only their volume
      changing. That is what makes the music adaptive: nothing is
      composed while your game runs.

  _mixdown.wav
      All the layers summed, for listening to. Your game should not
      use this one: mixing the layers yourself is the entire point.

  hum.json
      The tempo, the bar length, the loop length, and the pressure
      each layer arrives at. This is what your engine needs to fade
      them in and out and to switch tracks on a bar boundary.


THE TRACK
${"-".repeat(60)}

  Name        ${piece.name}
  Key         ${piece.key}
  Tempo       ${piece.tempo} bpm
  Chords      ${piece.progression}
  Seed        ${piece.seed ?? "unset"}
${piece.link ? `  Regenerate  ${piece.link}\n` : ""}

LICENCE
${"-".repeat(60)}

Creative Commons Attribution 4.0 International (CC BY 4.0)
https://creativecommons.org/licenses/by/4.0/

You are free to:

  * use this audio in anything, including commercial work
  * change it, cut it up, remix it, build on it
  * redistribute it

On one condition: you must give credit. Somewhere a person can find
it, such as your credits screen, your store page, or your readme:

    ${CREDIT_LINE}

If you changed the audio, say so. You do not need to ask permission,
and you do not owe anything else.

You may not add restrictions that stop anyone else doing the same.

This is a summary in plain words, not the licence itself. The licence
is the one at the link above, and it is what applies.
`;
}

/** The line to paste into a credits screen. */
export function creditLine(): string {
  return CREDIT_LINE;
}
